import { NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { CBDialogViewShower } from "../Custom components/CB/CBDialogViewShower"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHCheckbox } from "../Custom components/RH/RHCheckbox"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDatePicker } from "../Custom components/RH/RHDatePicker"
import { RHTextField } from "../Custom components/RH/RHTextField"
import { RHButtons, RHColors, RHDialogs, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHContractsViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { RHContractEntry } from "../SocketClientFunctions"
import { ContractEditDialogMode, ContractEditDialogState } from "./ContractEditDialogState"


export class ContractEditDialogView extends UIView {

    _mode: ContractEditDialogMode = "pending"
    isReadOnly = NO
    _cancellationConfirmationQuestion = ""
    _winningOfferAmount?: number

    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Lepingu sisestamine",
            textColor: RHColors.textPrimary,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontWeight: "600"
            }
        })
        .addedAsSubviewToView(this)

    readonly requirementsLabel = new UITextView()
        .configuredWithObject({
            text: "* Salvestamiseks täida lepingu sõlmimise kuupäev ja maksumus. " +
                "Mittesõlmimisel on kohustuslik põhjus.",
            isSingleLine: NO,
            textColor: RHColors.textPrimary
        })
        .addedAsSubviewToView(this)

    readonly errorMessageLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: NO,
            textColor: RHColor.danger
        })

    readonly unwrittenContractCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: { text: "Leping ei ole kirjalikus vormis" }
        })
        .addedAsSubviewToView(this)

    readonly agreedAmountLabel = new UITextView(undefined, UITextView.type.label)
        .configuredWithObject({
            text: "Lepingu maksumus",
            backgroundColor: UIColor.transparentColor
        })
        .addedAsSubviewToView(this)

    readonly agreedAmountField = new RHTextField()
        .configuredWithObject({
            type: "number",
            allowDecimals: YES,
            usesCommaAsDecimalSeparator: YES,
            minValue: 0,
            isRequired: YES,
            placeholderText: "Sisesta lepingu maksumus"
        })
        .addedAsSubviewToView(this)

    readonly amountDifferenceWarningLabel = new UITextView()
        .configuredWithObject({
            text: "Lepingu maksumus erineb võitnud pakkumusest",
            isSingleLine: NO,
            textColor: RHColor.warningOrange
        })

    readonly contractNumberLabel = new UITextView(undefined, UITextView.type.label)
        .configuredWithObject({
            text: "Lepingu number",
            backgroundColor: UIColor.transparentColor
        })
        .addedAsSubviewToView(this)

    readonly contractNumberField = new RHTextField()
        .configuredWithObject({
            placeholderText: "Sisesta lepingu number"
        })
        .addedAsSubviewToView(this)

    readonly signingDateLabel = new UITextView(undefined, UITextView.type.label)
        .configuredWithObject({
            text: "Lepingu sõlmimise kuupäev",
            backgroundColor: UIColor.transparentColor
        })
        .addedAsSubviewToView(this)

    readonly signingDatePicker = new RHDatePicker()
        .configuredWithObject({
            placeholder: "Vali kuupäev"
        })
        .addedAsSubviewToView(this)

    readonly contractEndDateLabel = new UITextView(undefined, UITextView.type.label)
        .configuredWithObject({
            text: "Lepingu lõpptähtaeg",
            backgroundColor: UIColor.transparentColor
        })
        .addedAsSubviewToView(this)

    readonly contractEndDatePicker = new RHDatePicker()
        .configuredWithObject({
            type: "date",
            placeholder: "Vali kuupäev (valikuline)"
        })
        .addedAsSubviewToView(this)

    // Required together with "Ei sõlminud lepingut" — Hanke seisundid ja
    // filtrid v3.1 needs an audited reason to distinguish "Lõpetatud
    // lepinguta" from a later contract termination (see WP1's Contract
    // nonConclusionReason field).
    readonly nonConclusionReasonLabel = new UITextView(undefined, UITextView.type.label)
        .configuredWithObject({
            text: "Lepingu mittesõlmimise põhjus",
            backgroundColor: UIColor.transparentColor
        })
        .addedAsSubviewToView(this)

    readonly nonConclusionReasonField = new RHTextField()
        .configuredWithObject({
            placeholderText: "Nt pakkuja loobus või tingimustes ei jõutud kokkuleppele"
        })
        .addedAsSubviewToView(this)

    readonly saveButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Salvesta",
                style: {
                    fontSize: RHButtons.compactFontSize
                }
            },
            variant: "success",
            contentPadding: RHButtons.compactContentPadding
        })
        .addedAsSubviewToView(this)

    readonly cancelContractButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Ei sõlminud lepingut",
                style: {
                    fontSize: RHButtons.compactFontSize
                }
            },
            variant: RHButtons.exitUtilityVariant,
            contentPadding: RHButtons.compactContentPadding
        })
        .addedAsSubviewToView(this)

    readonly completeContractButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Märgi leping lõpetatuks",
                style: {
                    fontSize: RHButtons.compactFontSize
                }
            },
            variant: "primary",
            contentPadding: RHButtons.compactContentPadding
        })
        .addedAsSubviewToView(this)

    readonly closeButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Sulge",
                style: {
                    fontSize: RHButtons.compactFontSize
                }
            },
            variant: RHButtons.destructiveUtilityVariant,
            contentPadding: RHButtons.compactContentPadding
        })
        .addedAsSubviewToView(this)


    constructor(elementID?: string) {
        super(elementID)

        this.unwrittenContractCheckbox.controlEventTargetAccumulator.SelectionChange = () => {
            this._updateContractNumberVisibility()
            this._updateErrorMessage()
        }
        this.agreedAmountField.controlEventTargetAccumulator.TextChange = () => {
            this._updateErrorMessage()
            this._updateAmountDifferenceWarningVisibility()
        }
        this.contractNumberField.controlEventTargetAccumulator.TextChange = () => this._updateErrorMessage()
        this.signingDatePicker.addTargetForControlEvent(
            RHDatePicker.controlEvent.DateChange, () => this._updateErrorMessage()
        )
        this.contractEndDatePicker.addTargetForControlEvent(
            RHDatePicker.controlEvent.DateChange, () => this._updateErrorMessage()
        )
        this.nonConclusionReasonField.controlEventTargetAccumulator.TextChange = () => this._updateErrorMessage()
    }


    get allowsSaving(): boolean {
        return !this.isReadOnly && ContractEditDialogState.allowsSaving(this._mode)
    }


    get allowsCancellation(): boolean {
        return !this.isReadOnly && ContractEditDialogState.allowsCancellation(this._mode)
    }


    get allowsCompletion(): boolean {
        return !this.isReadOnly && ContractEditDialogState.allowsCompletion(this._mode)
    }


    /** Trimmed non-conclusion reason, or undefined when blank — the caller validates before submitting. */
    get enteredNonConclusionReason(): string | undefined {
        return this.nonConclusionReasonField.text.trim() || undefined
    }


    get contractDetails(): {
        contractNumber?: string
        signedAt?: string
        contractEndDate: string | null
        isWritten: boolean
        agreedAmount: number
    } {
        let contractNumber: string | undefined
        if (!this.unwrittenContractCheckbox.selected) {
            contractNumber = this.contractNumberField.text.trim() || undefined
        }
        return {
            contractNumber,
            signedAt: this.signingDatePicker.selectedDate?.toISOString(),
            contractEndDate: this.contractEndDatePicker.selectedDate?.toISOString() ?? null,
            isWritten: !this.unwrittenContractCheckbox.selected,
            agreedAmount: this.agreedAmountField.numericValue
        }
    }


    _updateContractNumberVisibility() {
        this.contractNumberLabel.hidden = this.unwrittenContractCheckbox.selected
        this.contractNumberField.hidden = this.unwrittenContractCheckbox.selected
        this.setNeedsLayoutUpToRootView()
    }


    populateFromEntry(entry: RHContractEntry) {
        this._mode = ContractEditDialogState.modeForEntry(entry)
        this._winningOfferAmount = undefined
        if (entry.winningOfferAmount !== undefined &&
            Number.isFinite(entry.winningOfferAmount) && entry.winningOfferAmount >= 0) {
            this._winningOfferAmount = entry.winningOfferAmount
        }
        this._updateErrorMessage()

        let procurementPartDescription = `hanke „${RHTextSanitizer.escapedHTML(entry.procurement.title)}“`
        if (entry.procurementPartCount > 1) {
            procurementPartDescription += ` osa ${entry.winningPartNumbers.join(", ")}`
        }
        const offererCompanyName = RHTextSanitizer.escapedHTML(entry.offererCompany.name)
        this._cancellationConfirmationQuestion =
            `Kas kinnitad, et ${procurementPartDescription} kohta ei ole ettevõttega ${offererCompanyName} ` +
            "lepingut sõlmitud ka suuliselt ega tellimusega? Lepingut ei sõlmita ja toiming on lõplik."
        if (this._mode === "signed") {
            this._cancellationConfirmationQuestion =
                `Kas soovid tühistada ${procurementPartDescription} lepingu ettevõttega ${offererCompanyName}? ` +
                "Toiming on lõplik."
        }

        const requiredMarker = ` <span style="color: ${RHColor.danger.stringValue}">*</span>`
        this.contractNumberLabel.text = "Lepingu number"
        this.signingDateLabel.text = "Lepingu sõlmimise kuupäev"
        this.agreedAmountLabel.text = `Lepingu maksumus (${RHTextSanitizer.escapedHTML(entry.currency ?? "EUR")}, km-ta)`
        this.nonConclusionReasonLabel.text = "Lepingu mittesõlmimise põhjus"
        this.cancelContractButton.titleLabel.text = "Ei sõlminud lepingut"
        this.requirementsLabel.text = "* Salvestamiseks täida lepingu sõlmimise kuupäev ja maksumus. " +
            "Mittesõlmimisel on kohustuslik põhjus."
        if (this._mode === "signed") {
            this.cancelContractButton.titleLabel.text = "Tühista leping"
            this.nonConclusionReasonLabel.text = "Lepingu tühistamise põhjus"
            this.requirementsLabel.text = "* Salvestamiseks täida lepingu sõlmimise kuupäev ja maksumus. " +
                "Tühistamisel on kohustuslik põhjus."
        }
        if (this._mode === "cancelled") {
            this.nonConclusionReasonLabel.text = "Lepingu mittesõlmimise või tühistamise põhjus"
        }
        if (this.allowsCancellation || this._mode === "cancelled") {
            this.nonConclusionReasonLabel.addedAsSubviewToView(this)
            this.nonConclusionReasonField.addedAsSubviewToView(this)
        }
        else {
            this.nonConclusionReasonLabel.removeFromSuperview()
            this.nonConclusionReasonField.removeFromSuperview()
        }
        this.requirementsLabel.hidden = !this.allowsSaving
        if (this.allowsSaving) {
            this.agreedAmountLabel.innerHTML += requiredMarker
            this.signingDateLabel.innerHTML += requiredMarker
        }
        if (this.allowsCancellation) {
            this.nonConclusionReasonLabel.innerHTML += requiredMarker
        }

        let titlePrefix = "Lepingu sisestamine"
        if (this._mode === "signed") {
            titlePrefix = "Lepingu muutmine"
        }
        else if (this._mode === "completed") {
            titlePrefix = "Lõpetatud leping"
        }
        else if (this._mode === "cancelled") {
            titlePrefix = "Lepingu vaatamine"
        }
        if (this.isReadOnly && this._mode !== "completed") {
            titlePrefix = "Lepingu vaatamine"
        }

        const partNumbers = entry.winningPartNumbers ?? []
        if (entry.procurementPartCount > 1 && partNumbers.length > 0) {
            let partLabel = "Osad"
            if (partNumbers.length === 1) {
                partLabel = "Osa"
            }
            this.titleLabel.text =
                `${titlePrefix} · ${partLabel} ${partNumbers.join(", ")}`
        }
        else {
            this.titleLabel.text = titlePrefix
        }
        this.unwrittenContractCheckbox.configureWithObject({
            selected: entry.isWritten === NO,
            enabled: this.allowsSaving
        })
        let agreedAmount = entry.agreedAmount
        if (agreedAmount == null && this._mode === "pending" && this.allowsSaving) {
            agreedAmount = this._winningOfferAmount
        }
        this.agreedAmountField.configureWithObject({
            text: String(agreedAmount ?? ""),
            enabled: this.allowsSaving
        })
        this._updateAmountDifferenceWarningVisibility()
        this._updateContractNumberVisibility()
        this.contractNumberField.text = entry.contractNumber ?? ""
        if (entry.signedAt) {
            this.signingDatePicker.selectedDate = new Date(entry.signedAt)
        }
        else {
            this.signingDatePicker.selectedDate = undefined
        }
        if (entry.contractEndDate) {
            this.contractEndDatePicker.selectedDate = new Date(entry.contractEndDate)
        }
        else {
            this.contractEndDatePicker.selectedDate = undefined
        }
        this.nonConclusionReasonField.text = entry.nonConclusionReason ?? ""

        this.contractNumberField.enabled = this.allowsSaving
        this.signingDatePicker.enabled = this.allowsSaving
        this.contractEndDatePicker.enabled = this.allowsSaving
        this.nonConclusionReasonField.enabled = this.allowsCancellation
        this.saveButton.hidden = !this.allowsSaving
        this.cancelContractButton.hidden = !this.allowsCancellation
        this.completeContractButton.hidden = !this.allowsCompletion
    }


    async saveContractDetailsInDialog(
        dialogShower: CBDialogViewShower,
        saveContractDetails: () => Promise<void>
    ) {
        if (!this.allowsSaving || !this.saveButton.enabled) {
            return
        }
        const selectedDate = this.signingDatePicker.selectedDate
        if (!selectedDate) {
            this.signingDatePicker.isValid = NO
            this._updateErrorMessage("Lepingu sõlmimise kuupäev on kohustuslik.")
            return
        }
        if (selectedDate && Number.isNaN(selectedDate.getTime())) {
            this.signingDatePicker.isValid = NO
            this._updateErrorMessage("Lepingu sõlmimise kuupäev on vigane.")
            return
        }

        const contractEndDate = this.contractEndDatePicker.selectedDate
        if (contractEndDate && Number.isNaN(contractEndDate.getTime())) {
            this.contractEndDatePicker.isValid = NO
            this._updateErrorMessage("Lepingu lõpptähtaeg on vigane.")
            return
        }

        if (!Number.isFinite(this.agreedAmountField.numericValue) || this.agreedAmountField.numericValue < 0) {
            this.agreedAmountField.isValid = NO
            this._updateErrorMessage("Sisesta korrektne lepingu maksumus.")
            this.agreedAmountField.focus()
            return
        }

        this._updateErrorMessage()
        const controls = [
            this.unwrittenContractCheckbox, this.agreedAmountField,
            this.contractNumberField, this.signingDatePicker, this.contractEndDatePicker, this.nonConclusionReasonField,
            this.saveButton, this.cancelContractButton, this.completeContractButton
        ]
        controls.forEach(control => control.enabled = NO)
        try {
            await saveContractDetails()
            dialogShower.dialogView.dismiss()
        }
        catch (error) {
            this._showSaveError(error)
        }
        finally {
            controls.forEach(control => control.enabled = YES)
        }
    }


    _updateAmountDifferenceWarningVisibility() {
        const enteredAmount = this.agreedAmountField.numericValue
        if (this.allowsSaving && this._winningOfferAmount !== undefined &&
            Number.isFinite(enteredAmount) && enteredAmount !== this._winningOfferAmount) {
            this.amountDifferenceWarningLabel.addedAsSubviewToView(this)
        }
        else {
            this.amountDifferenceWarningLabel.removeFromSuperview()
        }
        this.setNeedsLayoutUpToRootView()
    }


    _updateErrorMessage(message?: string) {
        if (message) {
            this.errorMessageLabel.text = RHTextSanitizer.escapedHTML(message)
            this.errorMessageLabel.addedAsSubviewToView(this)
        }
        else {
            this.errorMessageLabel.removeFromSuperview()
        }
        this.setNeedsLayoutUpToRootView()
    }


    /** @boundary Reads the message off whatever the save threw. */
    _showSaveError(error: unknown) {
        let message = "Lepingu salvestamine ebaõnnestus."
        if (error instanceof Error && error.message) {
            message = error.message
        }
        this._updateErrorMessage(message)
    }


    replaceDialogWithCancellationConfirmation(
        dialogShower: CBDialogViewShower,
        cancellationWasConfirmed: () => Promise<void>
    ) {
        if (!this.allowsCancellation || !this.cancelContractButton.enabled) {
            return
        }
        if (!this.enteredNonConclusionReason) {
            this.nonConclusionReasonField.isValid = NO
            let reasonValidationMessage = "Lepingu mittesõlmimise põhjus on kohustuslik."
            if (this._mode === "signed") {
                reasonValidationMessage = "Lepingu tühistamise põhjus on kohustuslik."
            }
            this._updateErrorMessage(reasonValidationMessage)
            this.nonConclusionReasonField.focus()
            return
        }
        this._updateErrorMessage()
        const confirmationShower = CBDialogViewShower.replaceDialogWithQuestion(
            dialogShower,
            this._cancellationConfirmationQuestion,
            async () => {
                try {
                    await cancellationWasConfirmed()
                }
                catch (error) {
                    this._showSaveError(error)
                    throw error
                }
            }
        )
        confirmationShower.dialogView.view.yesButton.titleLabel.text = "Kinnita mittesõlmimine"
        if (this._mode === "signed") {
            confirmationShower.dialogView.view.yesButton.titleLabel.text = "Tühista leping"
        }
        confirmationShower.dialogView.view.noButton.titleLabel.text = "Tagasi"
    }


    override layoutSubviews() {
        super.layoutSubviews()

        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const fieldHeight = RHButtons.compactHeight
        const buttonHeight = RHButtons.compactHeight

        const actionButtons: RHButton[] = []
        if (this.allowsCompletion) {
            actionButtons.push(this.completeContractButton)
        }
        if (this.allowsSaving) {
            actionButtons.push(this.saveButton)
        }
        if (this.allowsCancellation) {
            actionButtons.push(this.cancelContractButton)
        }
        actionButtons.push(this.closeButton)

        this.contentBoundsWithInset(RHSpacing.large)
            .rectangleWithHeight(RHButtons.compactHeight)
            .assignedAsFrameOfView(this.titleLabel)
            .IF(this.allowsSaving)
            .rectangleForNextRow(RHSpacing.small, this.requirementsLabel)
            .assignedAsFrameOfView(this.requirementsLabel)
            .ENDIF()
            .rectangleForNextRow(RHSpacing.medium, fieldHeight)
            .assignedAsFrameOfView(this.unwrittenContractCheckbox)
            .IF(!this.unwrittenContractCheckbox.selected)
            .rectangleForNextRow(padding, labelHeight)
            .assignedAsFrameOfView(this.contractNumberLabel)
            .rectangleForNextRow(padding * 0.5, fieldHeight)
            .assignedAsFrameOfView(this.contractNumberField)
            .ENDIF()
            .rectangleForNextRow(padding, labelHeight)
            .assignedAsFrameOfView(this.signingDateLabel)
            .rectangleForNextRow(padding * 0.5, fieldHeight)
            .assignedAsFrameOfView(this.signingDatePicker)
            .rectangleForNextRow(padding, labelHeight)
            .assignedAsFrameOfView(this.contractEndDateLabel)
            .rectangleForNextRow(padding * 0.5, fieldHeight)
            .assignedAsFrameOfView(this.contractEndDatePicker)
            .rectangleForNextRow(padding, labelHeight)
            .assignedAsFrameOfView(this.agreedAmountLabel)
            .rectangleForNextRow(padding * 0.5, fieldHeight)
            .assignedAsFrameOfView(this.agreedAmountField)
            .IF(this.amountDifferenceWarningLabel.superview === this)
            .rectangleForNextRow(RHSpacing.small, this.amountDifferenceWarningLabel)
            .assignedAsFrameOfView(this.amountDifferenceWarningLabel)
            .ENDIF()
            .IF(this.nonConclusionReasonField.superview === this)
            .rectangleForNextRow(padding, labelHeight)
            .assignedAsFrameOfView(this.nonConclusionReasonLabel)
            .rectangleForNextRow(padding * 0.5, fieldHeight)
            .assignedAsFrameOfView(this.nonConclusionReasonField)
            .ENDIF()
            .IF(this.errorMessageLabel.superview === this)
            .rectangleForNextRow(RHSpacing.small, this.errorMessageLabel)
            .assignedAsFrameOfView(this.errorMessageLabel)
            .ENDIF()
            .rectangleForNextRow(RHSpacing.large, buttonHeight)
            .IF(actionButtons.length === 1)
            .rectangleWithWidth(RHDialogs.confirmationAlertButtonWidth, 0.5)
            .ENDIF()
            .distributeViewsEquallyAlongWidth(actionButtons, RHSpacing.small)
    }


    override intrinsicContentWidth(_constrainingHeight?: number) {
        return RHContractsViewTokens.editDialogContentWidth
    }


}
